/**
 * PHASED SETTINGS SETS (2026-10-10, docs/bim-plan/32 §3 A; Matt: "why not integrate phase
 * assignments into these settings directly?"). A Project Settings GROUP can hold more than one
 * SET of values, each built in a phase (and optionally demolished in one), the same two facts
 * an element carries. An element that follows Project Settings reads the set STANDING in the
 * phase being viewed, so an existing roof shows its old eaves in the Existing view and the new
 * eaves in New Construction, with nothing edited on the roof.
 *
 * - The group's ORIGINAL values stay where they always lived (settings `trimStyle.eaves` for the
 *   eaves group): that is the base set, standing until another set is built. A model with no
 *   extra sets is therefore unchanged, byte for byte, with no migration.
 * - Extra sets live in settings `styleSets` = { <group>: StyleSet[] }, each a FULL copy of the
 *   group's values (made by "New set from this one"), never a patch.
 * - Standing at phase P: the set built latest at or before P and not demolished by P; else the
 *   base set.
 * - An element can PIN a set ("Keep <set>"): instance param `styleSetPins` = { <group>: setId },
 *   with 'base' meaning the original values.
 *
 * Groups this round: 'eaves' (Roofs & Eaves). The mechanism is generic so Exterior / Interior
 * trim and Windows & Doors follow without a second design.
 *
 * Pure: reads plain settings + phases, never the doc (doc.ts calls in, which keeps the import
 * graph acyclic). The RESOLVE PHASE is an ambient value set around a read with
 * `withResolvePhase` — every element read in this process happens synchronously inside one, or
 * reads the default (the model's LAST phase, Revit's default view phase).
 */
import type { PhaseRecord } from './types'

export type StyleSetGroup = 'eaves'
export const STYLE_SET_GROUPS: StyleSetGroup[] = ['eaves']
export const STYLE_SETS_KEY = 'styleSets'
export const BASE_SET_ID = 'base'

export interface StyleSet {
  id: string
  name: string
  phaseCreated: string
  phaseDemolished?: string
  /** the group's full values (e.g. an EaveStyle), normalized by the group's own reader */
  values: Record<string, unknown>
}
export type StyleSetsSettings = Partial<Record<StyleSetGroup, StyleSet[]>>

export function normalizeStyleSets(raw: unknown): StyleSetsSettings {
  const r = (raw || {}) as Record<string, unknown>
  const out: StyleSetsSettings = {}
  for (const g of STYLE_SET_GROUPS) {
    const arr = Array.isArray(r[g]) ? (r[g] as unknown[]) : []
    const sets: StyleSet[] = []
    for (const v of arr) {
      const s = (v || {}) as Partial<StyleSet>
      if (typeof s.id !== 'string' || !s.id || s.id === BASE_SET_ID) continue
      if (typeof s.phaseCreated !== 'string' || !s.phaseCreated) continue
      sets.push({
        id: s.id,
        name: typeof s.name === 'string' && s.name.trim() ? s.name.trim() : 'Set',
        phaseCreated: s.phaseCreated,
        ...(typeof s.phaseDemolished === 'string' && s.phaseDemolished ? { phaseDemolished: s.phaseDemolished } : {}),
        values: s.values && typeof s.values === 'object' ? { ...(s.values as Record<string, unknown>) } : {},
      })
    }
    if (sets.length) out[g] = sets
  }
  return out
}

const orderOf = (phases: PhaseRecord[], id: string | null | undefined): number | null => {
  if (!id) return null
  const p = phases.find((ph) => ph.id === id)
  return p ? p.order : null
}
const lastPhaseId = (phases: PhaseRecord[]): string | null => {
  let best: PhaseRecord | null = null
  for (const p of phases) if (!best || p.order > best.order) best = p
  return best ? (best.id as string) : null
}

/** the set standing in `phaseId` (null = the base set). A set whose built-in phase was deleted
 *  never stands (it can't be placed in time); `phaseId` absent = the model's last phase. */
export function standingSet(
  sets: StyleSet[] | undefined, phases: PhaseRecord[], phaseId: string | null | undefined,
): StyleSet | null {
  if (!sets?.length || !phases.length) return null
  const view = orderOf(phases, phaseId ?? lastPhaseId(phases)) ?? orderOf(phases, lastPhaseId(phases))
  if (view == null) return null
  let best: StyleSet | null = null
  let bestOrder = -Infinity
  for (const s of sets) {
    const c = orderOf(phases, s.phaseCreated)
    if (c == null || c > view) continue
    const d = orderOf(phases, s.phaseDemolished)
    if (d != null && d <= view) continue
    if (c >= bestOrder) { best = s; bestOrder = c }
  }
  return best
}

/** one element's set for a group: its pin if it has one (a missing pinned set falls back to the
 *  base), else the standing set. null = the base values. */
export function setForElement(
  all: StyleSetsSettings, group: StyleSetGroup, pins: unknown, phases: PhaseRecord[], phaseId: string | null | undefined,
): StyleSet | null {
  const sets = all[group]
  const pin = pins && typeof pins === 'object' ? (pins as Record<string, unknown>)[group] : undefined
  if (typeof pin === 'string' && pin) {
    if (pin === BASE_SET_ID) return null
    return sets?.find((s) => s.id === pin) ?? null
  }
  return standingSet(sets, phases, phaseId)
}

/** A key naming which set stands in every group at `phaseId` — '' when the model has no extra
 *  sets at all, so every cache keyed on it behaves exactly as before sets existed. Two phases
 *  with the same standing sets share a key (and so share cached geometry). */
export function styleSetSignature(all: StyleSetsSettings, phases: PhaseRecord[], phaseId: string | null | undefined): string {
  let any = false
  const parts: string[] = []
  for (const g of STYLE_SET_GROUPS) {
    if (!all[g]?.length) { parts.push(BASE_SET_ID); continue }
    any = true
    parts.push(standingSet(all[g], phases, phaseId)?.id ?? BASE_SET_ID)
  }
  return any ? parts.join('|') : ''
}

// ---------- the ambient resolve phase ----------
let ambient: { phase: string | null } | null = null

/** run `fn` with element reads resolving Project Settings sets as of `phaseId` */
export function withResolvePhase<T>(phaseId: string | null | undefined, fn: () => T): T {
  const prev = ambient
  ambient = { phase: phaseId ?? null }
  try { return fn() } finally { ambient = prev }
}
/** the phase element reads currently resolve sets at (null = the model's last phase) */
export function currentResolvePhase(): string | null {
  return ambient?.phase ?? null
}
